import type { ShapeSelectionChangeMeta } from '@/components/app/shapes/shape-manager'
import type { ShapeSelectionHistoryMeta } from '@/lib/shapes/types/shape-selection-history-meta'
import type { TextRunStyleUpdate } from '@/lib/text/types/text-run-style-update'
import { createRequiredContext } from './create-required-context'

export interface ShapeNudgeItem {
  shapeId: string
  before: { x: number; y: number }
  after: { x: number; y: number }
}

/**
 * Every user command the editor exposes, other than the persistence path (shape commit/create/
 * delete, layer order and chain links, which stay explicit props). Built with `useStableCallbacks`
 * in home, so **the value and every function in it keep the same identity for the editor's
 * lifetime** and reading it never re-renders a consumer. Whether a command is currently available
 * lives in `EditorCommandState`, not here.
 */
export interface EditorCommands {
  // App and spread navigation
  closeEditor: () => void
  logout: () => void
  menuToggle: (isOpen: boolean) => void
  navigateSpread: (direction: 'prev' | 'next') => void
  changeSpreadIndex: (index: number) => void

  // Edit menu
  undo: () => void
  redo: () => void
  cut: () => void
  copy: () => void
  duplicate: () => void
  paste: () => void
  pasteInPlace: () => void
  deleteSelection: () => void
  selectAll: () => void
  selectNone: () => void
  checkSpelling: () => void

  // Object menu
  group: () => void
  ungroup: () => void
  lockSelected: () => void
  unlockSelected: () => void
  freezeSelected: () => void
  unfreezeSelected: () => void
  unlockAll: () => void
  unfreezeAll: () => void
  textOnPath: () => void
  convertTextToShape: () => void
  /** Edit Path on a given shape (convert a preset to a path and enter node edit). */
  editPath: (shapeId: string) => void
  /** Edit Path on the active object; check `EditorCommandState.canEditSelectedPath` first. */
  editSelectedPath: () => void

  // Zoom
  zoomIn: () => void
  zoomOut: () => void
  zoomFit: () => void
  actualSize: () => void
  zoomToSelection: () => void
  commitZoomPercent: (percent: number) => void

  // Photos
  addCaption: () => void
  cropPhoto: () => void
  togglePhotoEffects: () => void
  setPhotoEffectsOpen: (open: boolean) => void
  removePhotoFromPanel: () => void
  removeBackgroundFromPanel: () => void
  removePhotoFromShape: (shapeId: string) => void
  removeBackgroundFromShape: (shapeId: string) => void

  // Property panels and the shape-text edit bridge
  transparencyCommitApplied: () => void
  applyTextStyle: (update: TextRunStyleUpdate) => void
  applyKerningAtCaret: (value: number) => boolean
  restoreShapeTextFocus: () => void
  getEditingShapeTextSelectionStyle: () => TextRunStyleUpdate | null
  getEditingShapeTextId: () => string | null
  getEditingShapeTextKerningState: () => { available: boolean; value: number } | null

  // Canvas callbacks into home
  canvasShapeClick: (shapeId: string, isToggle?: boolean) => void
  canvasBackgroundClick: () => void
  canvasSelectionChange: (shapeIds: string[], meta?: ShapeSelectionChangeMeta) => void
  shapeSelectionHistory: (
    beforeIds: string[],
    afterIds: string[],
    meta?: ShapeSelectionHistoryMeta
  ) => void
  selectAllApplied: () => void
  selectNoneApplied: () => void
  activateTextTool: () => void
  activateSelectTool: () => void
  expandTypePanel: () => void
  shapeTextEditStateChange: () => void
  deleteShapesHandled: () => void
  shapesNudge: (items: ShapeNudgeItem[]) => void
  liveSelectionGeometrySync: () => void
  cornerResizeGestureActiveChange: (active: boolean) => void
  checkpointTextBoxesApplied: () => void
  getUndoStackSize: () => number

  // Node edit and pen tool
  exitNodeEditMode: () => void
  nodeEditModeChange: (isEditing: boolean, shapeId?: string) => void
  nodeEditPointTypeChange: (type: 'smooth' | 'corner') => void
  toggleNodeEditPenMode: (active: boolean) => void
  exitPenTool: () => void
}

const editorCommands = createRequiredContext<EditorCommands>('EditorCommands')

export const EditorCommandsContext = editorCommands.Context
export const EditorCommandsProvider = editorCommands.Provider
export const useEditorCommands = editorCommands.useRequiredContext
